import { useMemo, useState } from 'react';
import { KnownSearchResultItemDtoType, SearchResultItemDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Button, Typography } from '@mui/material';
import { SearchResultItem } from './result-items/search-result-item.component';
import {
  INITIAL_VISIBLE_PER_SECTION,
  PERSON_SUBTYPE_ORDER,
  PERSON_SUBTYPE_SECTION_TITLE,
  RESULT_TYPE_ORDER,
  RESULT_TYPE_SECTION_TITLE,
  SearchResultItemDtoSubType
} from './search.constants';
import { useSearchResultsStyles } from './search-results.styles';
import { convertHyphenated } from '../../../../core/util/string-conversion';

export interface SearchResultsProps {
  results: SearchResultItemDto[];
  currentProjectID?: string;
  onResultClick: (item: SearchResultItemDto) => void;
}

interface ResultGroup {
  /** Map key — may be a compound `user-or-account::<subType>` string for person sub-sections. */
  type: string;
  title: string;
  items: SearchResultItemDto[];
}

const USER_OR_ACCOUNT = KnownSearchResultItemDtoType.UserOrAccount;
const USER_OR_ACCOUNT_PREFIX = `${USER_OR_ACCOUNT}::`;

function sectionTitleForKey(key: string): string {
  if (key.startsWith(USER_OR_ACCOUNT_PREFIX)) {
    const subType = key.slice(USER_OR_ACCOUNT_PREFIX.length);
    return PERSON_SUBTYPE_SECTION_TITLE[subType] ?? convertHyphenated(subType, { capitalizeEachWord: true });
  }
  return RESULT_TYPE_SECTION_TITLE[key] ?? convertHyphenated(key, { capitalizeEachWord: true });
}

export function SearchResults({ results, currentProjectID, onResultClick }: SearchResultsProps) {
  // Group by type; user-or-account items are further split into per-subType sections.
  // This grouping occurs here in the frontend where this is a display concern.
  const groups = useMemo<ResultGroup[]>(() => {
    const byKey = new Map<string, SearchResultItemDto[]>();
    for (const item of results) {
      const key =
        item.type === USER_OR_ACCOUNT
          ? `${USER_OR_ACCOUNT_PREFIX}${item.subType ?? SearchResultItemDtoSubType.User}`
          : item.type;
      const list = byKey.get(key) ?? [];
      list.push(item);
      byKey.set(key, list);
    }

    // Expand RESULT_TYPE_ORDER: replace the user-or-account slot with per-subType keys in preferred order.
    const personKeys = PERSON_SUBTYPE_ORDER.map((st) => `${USER_OR_ACCOUNT_PREFIX}${st}`).filter((k) => byKey.has(k));
    const unknownPersonKeys = [...byKey.keys()].filter(
      (k) => k.startsWith(USER_OR_ACCOUNT_PREFIX) && !personKeys.includes(k)
    );

    const ordered: string[] = [];
    for (const type of RESULT_TYPE_ORDER) {
      if (type === USER_OR_ACCOUNT) {
        ordered.push(...personKeys, ...unknownPersonKeys);
      } else if (byKey.has(type)) {
        ordered.push(type);
      }
    }
    const extra = [...byKey.keys()].filter((k) => !ordered.includes(k));
    return [...ordered, ...extra].map((key) => ({
      type: key,
      title: sectionTitleForKey(key),
      items: byKey.get(key)!
    }));
  }, [results]);

  return (
    <>
      {groups.map((group) => (
        <SearchResultsSection
          key={group.type}
          group={group}
          currentProjectID={currentProjectID}
          onResultClick={onResultClick}
        />
      ))}
    </>
  );
}

function SearchResultsSection({
  group,
  currentProjectID,
  onResultClick
}: {
  group: ResultGroup;
  currentProjectID?: string;
  onResultClick: (item: SearchResultItemDto) => void;
}) {
  const classes = useSearchResultsStyles();
  const [expanded, setExpanded] = useState(false);

  const { title } = group;
  const total = group.items.length;
  const visible = expanded ? group.items : group.items.slice(0, INITIAL_VISIBLE_PER_SECTION);
  const hiddenCount = total - visible.length;

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          {title}
        </Typography>
        <Typography variant="caption" className={classes.sectionCount}>
          {expanded || total <= INITIAL_VISIBLE_PER_SECTION ? total : `${visible.length} of ${total}`}
        </Typography>
      </div>

      {visible.map((item) => (
        <SearchResultItem
          key={`${item.type}-${item.id}`}
          item={item}
          currentProjectID={currentProjectID}
          onNavigate={onResultClick}
        />
      ))}

      {hiddenCount > 0 && (
        <Button className={classes.showMore} size="small" color="primary" onClick={() => setExpanded(true)}>
          Show {hiddenCount} more
        </Button>
      )}
    </div>
  );
}
